import { useTimeSettings } from '@mnd-frontend/contexts';
import { dateTimeStringWithTz, shortDateTimeStringWithTz } from '@mnd-frontend/dates';
import { type MaterialStatus, Visibility } from '@mnd-frontend/graphql-schema';
import { useMediaQuery } from '@mnd-frontend/hooks';
import type { MaterialTypeUnion } from '@mnd-frontend/ts';
import {
  Avatar,
  Badge,
  Dropdown,
  Icons,
  materialStatusToBadgeVariant,
  Text,
  Tooltip,
  textStyles,
} from '@mnd-frontend/ui/publish/components';
import { media } from '@mnd-frontend/ui/publish/utils';
import {
  GREY_700,
  GREY_800,
  GREY_1000,
  SPACING_0_5,
  SPACING_1,
  SPACING_2,
} from '@mnd-frontend/ui/shared/tokens';
import React, { type ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { getMaterialCta, MaterialCta } from '../../components/MaterialList/pathnameHelpers';
import { getMaterialStatusLabel } from './translationHelpers';

const MaterialMediaWrapper = styled.div`
  margin-right: ${SPACING_2};
`;

const MaterialActionsWrapper = styled.div`
  margin-left: ${SPACING_2};
`;

const Time = styled.time`
  color: ${GREY_800};
`;

const MaterialActions = ({
  item,
  actions,
  renderLegacyActions,
}: {
  item: any;
  actions: any;
  renderLegacyActions: any;
}) => {
  const { id } = item;
  const options = React.useMemo(
    () =>
      actions &&
      actions.map((action: any) => ({
        ...action.option,
        text: action.label,
        action: action.onSelect && (() => action.onSelect(id)),
      })),
    [actions, id],
  );

  return (
    <MaterialActionsWrapper>
      {renderLegacyActions ? (
        renderLegacyActions(item)
      ) : (
        <Dropdown options={options} title="Actions" hideTitle />
      )}
    </MaterialActionsWrapper>
  );
};

const MaterialMetaWrapper = styled.div`
  & > *:not(:last-child) {
    margin-right: ${SPACING_1};
  }

  & time {
    display: block;
    ${media.greaterThan('phablet')} {
      display: inline;
    }
  }
`;

const MaterialMeta = ({
  status,
  dateTime,
  publishedBy,
}: {
  status: MaterialStatus;
  dateTime: any;
  publishedBy: any;
}) => {
  const [t] = useTranslation();
  const label = getMaterialStatusLabel(status, t);
  const large = useMediaQuery(media.greaterThan('tablet'));
  const {
    material: { timezone, locale },
  } = useTimeSettings();
  const name = publishedBy?.deleted
    ? t('publish_components_deleted-user')
    : publishedBy?.removedFromSource
      ? t('publish_components_removed-user')
      : publishedBy?.user?.isMynewsdeskAdmin
        ? 'Mynewsdesk'
        : publishedBy?.user?.name;

  return (
    <MaterialMetaWrapper>
      {label && <Badge variant={materialStatusToBadgeVariant(status)} text={label} />}
      {dateTime && (
        <Time dateTime={dateTime}>
          {large
            ? dateTimeStringWithTz(dateTime, timezone, locale)
            : shortDateTimeStringWithTz(dateTime, timezone, locale)}
        </Time>
      )}
      {name && (
        <Text as="span" variant="body" color="GREY_800">
          {t('publish_components_published-by', {
            name,
          })}
        </Text>
      )}
    </MaterialMetaWrapper>
  );
};

const MaterialStatusIcon = styled.div`
  display: block;
  padding: 0.625rem;
`;

const MaterialStatusIconGroup = styled.div`
  display: block;
  min-width: 8.25rem;
  text-align: right;
`;

const MaterialStatusWrapper = styled.div<{ $small?: boolean }>`
  display: flex;
  align-items: center;
  margin-left: ${SPACING_2};
  & > *:not(:first-child) {
    margin-left: ${SPACING_1};
  }
  & > * {
    white-space: nowrap;
  }
  ${({ $small }) =>
    $small &&
    css`
      font-size: 0.875rem;
      & i {
        width: 1rem;
      }
      & ${MaterialStatusIcon} {
        padding-top: 0;
        padding-bottom: 0;
      }
      & ${MaterialStatusIconGroup} {
        text-align: left;
      }
    `}
`;

const MaterialStatusDetails = ({
  children,
  expiresAt,
  scheduledSendExclusive,
  visibility,
  small,
}: {
  children?: ReactNode;
  expiresAt?: any;
  scheduledSendExclusive?: boolean;
  visibility: Visibility;
  small?: boolean;
}) => {
  const [t] = useTranslation();
  const {
    material: { timezone, locale },
  } = useTimeSettings();

  return children ||
    expiresAt ||
    scheduledSendExclusive ||
    (visibility && visibility !== Visibility.Visible) ? (
    <MaterialStatusWrapper $small={small}>
      <MaterialStatusIconGroup>
        {expiresAt && (
          <Tooltip
            content={
              <Trans
                i18nKey="publish_components_status-icon_deletion-status"
                values={{
                  date: dateTimeStringWithTz(expiresAt, timezone, locale),
                }}
              />
            }
            placement="bottom"
          >
            <MaterialStatusIcon>
              <Icons.DeleteSchedule />
            </MaterialStatusIcon>
          </Tooltip>
        )}
        {scheduledSendExclusive && (
          <Tooltip
            content={t('publish_components_status-icon_send-exclusive-scheduled-status')}
            placement="bottom"
          >
            <MaterialStatusIcon>
              <Icons.EmailSchedule />
            </MaterialStatusIcon>
          </Tooltip>
        )}
        {visibility === Visibility.Hidden && (
          <Tooltip content={t('publish_components_status-icon_hidden-status')} placement="bottom">
            <MaterialStatusIcon>
              <Icons.VisibilityOff />
            </MaterialStatusIcon>
          </Tooltip>
        )}
        {visibility === Visibility.Pinned && (
          <Tooltip content={t('publish_components_status-icon_sticky-status')} placement="bottom">
            <MaterialStatusIcon>
              <Icons.Pin />
            </MaterialStatusIcon>
          </Tooltip>
        )}
      </MaterialStatusIconGroup>
      {children}
    </MaterialStatusWrapper>
  ) : null;
};

const MaterialBody = styled.div<{ $expanded?: boolean }>`
  margin-right: auto;
  & > * {
    margin-left: 0;
  }
  & a {
    text-decoration: none;
    color: ${GREY_1000};
  }

  ${({ $expanded }) =>
    $expanded &&
    css`
      height: 100%;
    `}
`;
const MaterialWrapper = styled.div`
  margin-right: auto;
  display: flex;
  align-items: center;
  flex-grow: 1;
`;
const MaterialHeader = styled.h2`
  ${textStyles.body}
  /* fallback overflow support for different browsers */
  overflow-wrap: break-word;
  word-wrap: break-word;
  word-break: break-word;
  margin: 0 0 ${SPACING_0_5};
  padding: 0;
  font-weight: 600;
  display: block;
  & > a {
    outline: none;
    pointer-events: none;
  }
`;

const MaterialCtaText = styled.div`
  && {
    color: ${props => props.theme.colorTextButtonTextTextButtonOnLightDefault};
    &:hover {
      color: ${props => props.theme.colorTextButtonTextTextButtonOnLightHover};
    }
  }
`;

const MaterialSubHeader = styled.span`
  display: block;
  color: ${GREY_700};
  text-transform: uppercase;
  font-size: 0.75rem;
`;

const MaterialItem = <
  T extends {
    __typename: MaterialTypeUnion;
    type?: MaterialTypeUnion;
    id: string;
    [key: string]: any;
  },
>(props: {
  item: T;
  url?: string;
  actions?: any;
  renderLegacyActions?: (item: T) => ReactNode;
  expanded?: boolean;
  expandedActions?: ReactNode;
  onClick?: () => void;
  hasAnalyzeEnabled?: boolean;
  hasAnalyzeLight?: boolean;
  hasPrImpact?: boolean;
  hasAnalyzeMaterial?: boolean;
  showMaterialType?: boolean;
}) => {
  const [t] = useTranslation();
  const {
    item,
    url,
    actions,
    renderLegacyActions,
    expanded,
    hasAnalyzeEnabled,
    hasAnalyzeLight,
    hasPrImpact,
    showMaterialType = false,
    hasAnalyzeMaterial,
    ...restProps
  } = props;
  const { header, status, publishedAt2, scheduledFor, expiresAt, quotationsCount, publishedBy } =
    item;

  let imageUrl: string | undefined | null;
  switch (item.__typename) {
    case 'Video':
      imageUrl = item.thumbnailUrl;
      break;
    case 'Document':
      imageUrl = item.preview;
      break;
    default:
      imageUrl = item.image?.url;
      break;
  }
  const visibility = 'visibility' in item ? item.visibility : null;
  const scheduledSendExclusive = 'sendExclusive' in item ? item.sendExclusive : null;
  const gtMedium = useMediaQuery(media.greaterThan('tablet'));

  const ctaText = (() => {
    switch (
      getMaterialCta({
        item,
        hasAnalyzeEnabled: Boolean(hasAnalyzeLight || hasAnalyzeEnabled),
        hasMediaHitsAccess: !!hasPrImpact,
        hasAnalyzeMaterial: !!hasAnalyzeMaterial,
      })
    ) {
      case MaterialCta.Edit:
        return <MaterialCtaText>{t('common_edit')}</MaterialCtaText>;
      case MaterialCta.MediaHits:
        return (
          <MaterialCtaText>{t('material_view_hits', { count: quotationsCount })}</MaterialCtaText>
        );
      case MaterialCta.AnalyzeMaterial:
        return <MaterialCtaText>Analyze</MaterialCtaText>;
      case MaterialCta.AnalyzeOverview:
        return (
          <MaterialCtaText>
            {quotationsCount === 0
              ? 'Analyze'
              : t('material_analyze_hits', { count: quotationsCount })}
          </MaterialCtaText>
        );
      case MaterialCta.Preview:
        return <MaterialCtaText>{t('publish_components_pr-list_action-preview')}</MaterialCtaText>;
    }
  })();

  return (
    <MaterialWrapper {...restProps}>
      <MaterialMediaWrapper>
        <Avatar type="thumbnail" image={imageUrl} />
      </MaterialMediaWrapper>
      <MaterialBody $expanded={expanded}>
        <MaterialHeader>
          {item.type && showMaterialType && <MaterialSubHeader>{item.type}</MaterialSubHeader>}
          <a href={url}>{header || t('common_untitled')}</a>
        </MaterialHeader>
        <MaterialMeta
          status={status}
          dateTime={publishedAt2 || scheduledFor}
          publishedBy={publishedBy}
        />
        {!gtMedium && (
          <MaterialStatusDetails
            expiresAt={expiresAt}
            scheduledSendExclusive={scheduledSendExclusive}
            visibility={visibility}
            small
          >
            {ctaText}
          </MaterialStatusDetails>
        )}
        {'expandedActions' in props && props.expandedActions}
      </MaterialBody>
      {gtMedium && (
        <MaterialStatusDetails
          expiresAt={expiresAt}
          scheduledSendExclusive={scheduledSendExclusive}
          visibility={visibility}
        >
          {url || props.onClick ? ctaText : null}
        </MaterialStatusDetails>
      )}
      {(actions || renderLegacyActions) && (
        <MaterialActions item={item} actions={actions} renderLegacyActions={renderLegacyActions} />
      )}
    </MaterialWrapper>
  );
};

export default MaterialItem;
